import { useEffect, useState, useMemo } from "react";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { BASE_URL } from "../../Config/api.config";

// Updated Types based on your API response
interface Ticket {
  id: string;
  ticket_number: string;
  client_id: string;
  category_id: string;
  category_name: string;
  category_description: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  admin_updated_by: string | null;
  admin_updated_at: string | null;
  client_name: string;
  client_email: string;
  client_phone: string | null;
  client_prefix_phone: string | null;
  message_count: string;
  last_message_date: string | null;
}

interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  message: string;
  is_internal: boolean;
  created_at: string;
  sender_name: string;
  sender_email: string;
  is_admin: boolean;
}

interface TicketCategory {
  id: string;
  name: string;
  description: string | null;
  available: number;
  created_at: string;
  es: string | null;
  en: string | null;
  description_es: string | null;
  description_en: string | null;
}

// Updated API Response Interface
interface TicketsApiResponse {
  status: string;
  result: {
    tickets: Ticket[];
    total: number;
    limit: number;
    offset: number;
    filters?: {
      status?: string;
      category_id?: string;
      priority?: string;
      search?: string;
      language?: string;
    };
    sort?: {
      by: string;
      order: string;
    };
  };
  _debug?: any;
}

// Summary interface for dashboard stats
interface TicketsSummary {
  total_tickets: number;
  open_tickets: number;
  closed_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  high_priority_tickets: number;
  today_tickets: number;
  week_tickets: number;
}

const TicketsAdminDashboard = () => {
  const [adminData] = useLocalStorage("adminData", "");
  const [data, setData] = useState<TicketsApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending";
  }>({
    key: "created_at",
    direction: "descending",
  });
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([]);
  const [activeTab, setActiveTab] = useState<"overview" | "tickets">("overview");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const fetchTickets = async (
    page: number = currentPage,
    limit: number = itemsPerPage,
    status: string = statusFilter,
    priority: string = priorityFilter,
    category: string = categoryFilter
  ) => {
    try {
      setLoading(true);
      setError(null);
      const offset = (page - 1) * limit;

      let url = `${BASE_URL}tickets?admin_tickets=1&limit=${limit}&offset=${offset}`;
      
      if (status !== "all") url += `&status=${status}`;
      if (priority !== "all") url += `&priority=${priority}`;
      if (category !== "all") url += `&category_id=${category}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: TicketsApiResponse = await response.json();

      if (result.status === "success") {
        setData(result);
      } else {
        // throw new Error(result.result?.error_msg || "Failed to fetch tickets");
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketMessages = async (ticketId: string) => {
    try {
      const response = await fetch(
        `${BASE_URL}tickets?admin_ticket=1&ticket_id=${ticketId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${adminData.uid}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === "success") {
        setTicketMessages(result.data?.messages || []);
      }
    } catch (err) {
      console.error("Error fetching ticket messages:", err);
    }
  };

  useEffect(() => {
    if (adminData?.uid) {
      fetchTickets();
    }
  }, [adminData]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchTickets(newPage, itemsPerPage, statusFilter, priorityFilter, categoryFilter);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchTickets(1, newItemsPerPage, statusFilter, priorityFilter, categoryFilter);
  };

  // Handle filter changes
  const handleFilterChange = () => {
    setCurrentPage(1);
    fetchTickets(1, itemsPerPage, statusFilter, priorityFilter, categoryFilter);
  };

  // Calculate summary stats from tickets data
  const calculateSummary = (tickets: Ticket[]): TicketsSummary => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    return {
      total_tickets: tickets.length,
      open_tickets: tickets.filter(t => t.status === 'open').length,
      closed_tickets: tickets.filter(t => t.status === 'closed').length,
      in_progress_tickets: tickets.filter(t => t.status === 'in_progress').length,
      resolved_tickets: tickets.filter(t => t.status === 'resolved').length,
      high_priority_tickets: tickets.filter(t => t.priority === 'high' || t.priority === 'urgent').length,
      today_tickets: tickets.filter(t => {
        const ticketDate = new Date(t.created_at);
        return ticketDate >= today;
      }).length,
      week_tickets: tickets.filter(t => {
        const ticketDate = new Date(t.created_at);
        return ticketDate >= oneWeekAgo;
      }).length,
    };
  };

  // Filter and sort tickets (client-side for current page only)
  const filteredAndSortedTickets = useMemo(() => {
    const tickets = data?.result?.tickets || [];
    
    let filtered = tickets.filter(
      (ticket) =>
        ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.client_email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort tickets
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof Ticket];
      let bValue: any = b[sortConfig.key as keyof Ticket];

      // Handle date sorting
      if (sortConfig.key.includes("date") || sortConfig.key.includes("created") || sortConfig.key.includes("updated")) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle numeric values
      if (sortConfig.key === "message_count") {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [data, searchTerm, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "0000-00-00 00:00:00") return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleTicketAction = async (ticketId: string, action: string, data?: any) => {
    try {
      const formData = new FormData();
      formData.append('_method', action);
      formData.append('ticket_id', ticketId);
      
      if (data) {
        Object.keys(data).forEach(key => {
          formData.append(key, data[key]);
        });
      }

      const response = await fetch(`${BASE_URL}tickets`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success") {
          // Refresh tickets list
          fetchTickets();
          if (selectedTicket?.id === ticketId) {
            // Refresh selected ticket details
            const updatedTickets = data?.result?.tickets || [];
            const updatedTicket = updatedTickets.find((t: Ticket) => t.id === ticketId);
            if (updatedTicket) {
              setSelectedTicket(updatedTicket);
              fetchTicketMessages(ticketId);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error performing ticket action:", err);
    }
  };

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const total = data?.result?.total || 0;
    const limit = data?.result?.limit || 20;
    const offset = data?.result?.offset || 0;
    const totalPages = Math.ceil(total / limit);
    const currentPage = Math.floor(offset / limit) + 1;
    const hasMore = currentPage < totalPages;

    return {
      total_tickets: total,
      current_page: currentPage,
      total_pages: totalPages,
      has_more: hasMore,
      current_page_tickets: data?.result?.tickets?.length || 0
    };
  }, [data]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const { current_page, total_pages } = paginationInfo;
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tickets data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchTickets()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    );
  }

  const tickets = data.result?.tickets || [];
  const summary = calculateSummary(tickets);
  const pageNumbers = getPageNumbers();

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Support Tickets Dashboard
          </h1>
          <p className="text-gray-600">
            Manage and monitor all support tickets
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("overview")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "overview"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                System Overview
              </button>
              <button
                onClick={() => setActiveTab("tickets")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "tickets"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Tickets ({paginationInfo.total_tickets})
              </button>
            </nav>
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Tickets
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {summary.total_tickets}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <span className="text-blue-600 text-xl">📋</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-green-600 font-medium">
                    {summary.today_tickets} today
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Open Tickets
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {summary.open_tickets}
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <span className="text-yellow-600 text-xl">🔓</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    {summary.in_progress_tickets} in progress
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Resolved & Closed
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {summary.resolved_tickets + summary.closed_tickets}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <span className="text-green-600 text-xl">✅</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    {summary.week_tickets} this week
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      High Priority
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {summary.high_priority_tickets}
                    </p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <span className="text-red-600 text-xl">🚨</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    Urgent attention needed
                  </span>
                </div>
              </div>
            </div>

            {/* Status Distribution */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Ticket Status Distribution
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { status: 'open', label: 'Open', count: summary.open_tickets, color: 'bg-blue-500' },
                  { status: 'in_progress', label: 'In Progress', count: summary.in_progress_tickets, color: 'bg-yellow-500' },
                  { status: 'resolved', label: 'Resolved', count: summary.resolved_tickets, color: 'bg-green-500' },
                  { status: 'closed', label: 'Closed', count: summary.closed_tickets, color: 'bg-gray-500' },
                ].map((item) => (
                  <div key={item.status} className="text-center">
                    <div className={`h-2 ${item.color} rounded-full mb-2`}></div>
                    <div className="text-2xl font-bold text-gray-900">{item.count}</div>
                    <div className="text-sm text-gray-600 capitalize">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "tickets" && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Categories</option>
                    {/* Categories would need to be fetched separately */}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleFilterChange}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                <div className="flex-1 w-full sm:max-w-md">
                  <input
                    type="text"
                    placeholder="Search tickets by number, subject, or client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>

                  <div className="text-sm text-gray-600">
                    Showing {filteredAndSortedTickets.length} of{" "}
                    {paginationInfo.current_page_tickets} tickets on this page
                    <br />
                    <span className="text-xs">
                      Total: {paginationInfo.total_tickets} tickets
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tickets Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        { key: "ticket_number", label: "Ticket #" },
                        { key: "subject", label: "Subject" },
                        { key: "client_name", label: "Client" },
                        { key: "status", label: "Status" },
                        { key: "priority", label: "Priority" },
                        { key: "category_name", label: "Category" },
                        { key: "created_at", label: "Created" },
                        { key: "message_count", label: "Messages" },
                      ].map(({ key, label }) => (
                        <th
                          key={key}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort(key)}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{label}</span>
                            {sortConfig.key === key && (
                              <span>
                                {sortConfig.direction === "ascending"
                                  ? "↑"
                                  : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedTickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedTicket(ticket);
                          fetchTicketMessages(ticket.id);
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ticket.ticket_number}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                            {ticket.subject}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {ticket.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{ticket.client_name}</div>
                            <div className="text-gray-500 text-xs">
                              {ticket.client_email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {ticket.category_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(ticket.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {ticket.message_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTicket(ticket);
                              fetchTicketMessages(ticket.id);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {paginationInfo.total_pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(paginationInfo.current_page - 1)}
                      disabled={paginationInfo.current_page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(paginationInfo.current_page + 1)}
                      disabled={!paginationInfo.has_more}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(paginationInfo.current_page - 1) * itemsPerPage + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(
                            paginationInfo.current_page * itemsPerPage,
                            paginationInfo.total_tickets
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {paginationInfo.total_tickets}
                        </span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => handlePageChange(paginationInfo.current_page - 1)}
                          disabled={paginationInfo.current_page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>←
                        </button>

                        {/* Page numbers */}
                        {pageNumbers.map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              paginationInfo.current_page === page
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(paginationInfo.current_page + 1)}
                          disabled={!paginationInfo.has_more}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>→
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Empty State */}
            {filteredAndSortedTickets.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📭</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No tickets found
                </h3>
                <p className="text-gray-600">
                  {searchTerm || statusFilter !== "all" || priorityFilter !== "all" || categoryFilter !== "all"
                    ? "Try adjusting your search criteria or filters"
                    : "No tickets available"}
                </p>
              </div>
            )}
          </div>
        )}


        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedTicket.subject}
                    </h2>
                    <p className="text-gray-600">{selectedTicket.ticket_number}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedTicket(null);
                      setTicketMessages([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Ticket Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Ticket Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedTicket.status)}`}>
                          {selectedTicket.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Priority:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(selectedTicket.priority)}`}>
                          {selectedTicket.priority}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Category:</span>
                        <span className="font-medium">{selectedTicket.category_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Created:</span>
                        <span className="font-medium">
                          {formatDate(selectedTicket.created_at)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Updated:</span>
                        <span className="font-medium">
                          {formatDate(selectedTicket.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Client Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Client Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Client Name:</span>
                        <span className="font-medium">{selectedTicket.client_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{selectedTicket.client_email}</span>
                      </div>
                      {selectedTicket.client_phone && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Phone:</span>
                          <span className="font-medium">{selectedTicket.client_phone}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Client ID:</span>
                        <span className="font-medium">#{selectedTicket.client_id}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ticket Description */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Description
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedTicket.description}
                    </p>
                  </div>
                </div>

                {/* Admin Actions */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Admin Actions
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTicket.status !== 'closed' && (
                      <>
                        <button
                          onClick={() => handleTicketAction(selectedTicket.id, 'ADMIN_UPDATE_STATUS', { status: 'in_progress' })}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                        >
                          Mark In Progress
                        </button>
                        <button
                          onClick={() => handleTicketAction(selectedTicket.id, 'ADMIN_UPDATE_STATUS', { status: 'resolved' })}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Mark Resolved
                        </button>
                        <button
                          onClick={() => handleTicketAction(selectedTicket.id, 'ADMIN_CLOSE_TICKET')}
                          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          Close Ticket
                        </button>
                      </>
                    )}
                    {selectedTicket.status === 'closed' && (
                      <button
                        onClick={() => handleTicketAction(selectedTicket.id, 'ADMIN_UPDATE_STATUS', { status: 'open' })}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Reopen Ticket
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Conversation ({ticketMessages.length} messages)
                  </h3>
                  {ticketMessages.length > 0 ? (
                    <div className="space-y-4">
                      {ticketMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`p-4 rounded-lg ${
                            message.is_admin 
                              ? 'bg-blue-50 border border-blue-200' 
                              : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-medium text-gray-900">
                              {message.sender_name}
                              {message.is_admin && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  Admin
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(message.created_at)}
                            </div>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {message.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No messages found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketsAdminDashboard;